import React, { useState, useEffect } from 'react';
import { XMarkIcon, MapPinIcon, ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { locationsApi } from '@/services/api';
import { useDispatch, useSelector } from 'react-redux';
import { setFilters, filterProducts } from '@/store/slices/productFiltersSlice';
import toast from 'react-hot-toast';

interface Location {
  id: number;
  name: string;
  location_type: 'country' | 'state' | 'city' | 'district' | 'area';
  parent_id: number | null;
}

interface LocationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedLocations: string[];
  onSelectLocations: (locations: string[]) => void;
}

const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedLocations,
  onSelectLocations,
}) => {
  const dispatch = useDispatch();
  const radiusFromState = useSelector((state: any) => state.productFilters.radius_km);
  const latitudeFromState = useSelector((state: any) => state.productFilters.latitude);
  const longitudeFromState = useSelector((state: any) => state.productFilters.longitude);

  const [countries, setCountries] = useState<Location[]>([]);
  const [states, setStates] = useState<Location[]>([]);
  const [cities, setCities] = useState<Location[]>([]);
  const [districts, setDistricts] = useState<Location[]>([]);
  const [areas, setAreas] = useState<Location[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<Location | null>(null);
  const [selectedState, setSelectedState] = useState<Location | null>(null);
  const [selectedCity, setSelectedCity] = useState<Location | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<Location | null>(null);

  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [areaSearchQuery, setAreaSearchQuery] = useState('');

  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // GPS search state
  const [radius, setRadius] = useState(radiusFromState || 50);
  const [useGPS, setUseGPS] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  // Parse selected locations to get IDs and names
  useEffect(() => {
    if (selectedLocations.length > 0) {
      // If locations are stored as "id:name" format, parse them
      // Otherwise, extract names for matching
      const ids: number[] = [];
      const names: string[] = [];
      
      selectedLocations.forEach(loc => {
        const match = loc.match(/^(\d+):(.+)$/);
        if (match) {
          const id = parseInt(match[1]);
          const name = match[2];
          ids.push(id);
          names.push(name);
        } else {
          // Just a name, no ID
          names.push(loc);
        }
      });
      
      setSelectedLocationIds(ids);
      // Store names for matching when IDs aren't available
      // We'll match by name in the toggle functions
    } else {
      setSelectedLocationIds([]);
    }
  }, [selectedLocations]);

  // Load initial data
  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      const [countriesRes, statesRes, citiesRes] = await Promise.all([
        locationsApi.getAll('country'),
        locationsApi.getAll('state'),
        locationsApi.getAll('city'),
      ]);

      setCountries(countriesRes.data || []);
      setStates(statesRes.data || []);
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle geolocation request
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        // Dispatch to Redux with GPS coordinates
        dispatch(setFilters({
          latitude: lat,
          longitude: lng,
          radius_km: radius,
          locations: [] // Clear hierarchical locations when using GPS
        }));

        // Trigger search after a short delay
        setTimeout(() => {
          dispatch(filterProducts());
        }, 100);

        setUseGPS(true);
        setIsLoadingLocation(false);
        toast.success(`Searching within ${radius}km of your location`);
      },
      (error) => {
        setIsLoadingLocation(false);
        console.error('Geolocation error:', error);

        let errorMessage = 'Unable to get your location. ';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please enable location permissions in your browser.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out.';
            break;
          default:
            errorMessage += 'An unknown error occurred.';
        }
        toast.error(errorMessage);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Handle radius change
  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRadius = parseInt(e.target.value);
    setRadius(newRadius);

    // If GPS is already active, update the filter
    if (useGPS || (latitudeFromState && longitudeFromState)) {
      dispatch(setFilters({
        latitude: latitudeFromState,
        longitude: longitudeFromState,
        radius_km: newRadius,
        locations: []
      }));
    }
  };

  // Handle clear GPS search
  const handleClearGPS = () => {
    setUseGPS(false);
    dispatch(setFilters({
      latitude: undefined,
      longitude: undefined,
      radius_km: 50,
    }));
    setRadius(50);
    toast.success('GPS search cleared');
  };

  const handleCountrySelect = async (country: Location) => {
    setSelectedCountry(country);
    setSelectedState(null);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setAreas([]);
    
    try {
      const statesRes = await locationsApi.getAll('state', country.id);
      setStates(statesRes.data || []);
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const handleStateSelect = async (state: Location) => {
    setSelectedState(state);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setAreas([]);
    
    try {
      const citiesRes = await locationsApi.getAll('city', state.id);
      setCities(citiesRes.data || []);
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const handleCitySelect = async (city: Location) => {
    setSelectedCity(city);
    setSelectedDistrict(null);
    
    try {
      // Get all children of the city (could be districts or areas)
      const childrenRes = await locationsApi.getChildren(city.id);
      const children = childrenRes.data || [];
      
      // Separate districts and areas
      const districtsData = children.filter(
        (loc: Location) => loc.location_type === 'district'
      );
      const areasData = children.filter(
        (loc: Location) => loc.location_type === 'area'
      );
      
      // If there are districts, show them; otherwise show areas directly
      if (districtsData.length > 0) {
        setDistricts(districtsData);
        setAreas([]);
      } else {
        setDistricts([]);
        setAreas(areasData);
      }
    } catch (error) {
      console.error('Error loading districts/areas:', error);
      setDistricts([]);
      setAreas([]);
    }
  };

  const handleDistrictSelect = async (district: Location) => {
    setSelectedDistrict(district);
    
    try {
      const areasRes = await locationsApi.getChildren(district.id);
      const areasData = (areasRes.data || []).filter(
        (loc: Location) => loc.location_type === 'area'
      );
      setAreas(areasData);
    } catch (error) {
      console.error('Error loading areas:', error);
    }
  };

  const handleLocationToggle = (location: Location) => {
    const locationString = `${location.id}:${location.name}`;
    setSelectedLocationIds(prev => {
      const isSelected = prev.includes(location.id) || 
        selectedLocations.some(loc => 
          loc === locationString || 
          loc === location.name || 
          loc.endsWith(`:${location.name}`) ||
          loc.startsWith(`${location.id}:`)
        );
      
      if (isSelected) {
        // Deselect
        const newIds = prev.filter(id => id !== location.id);
        const newLocations = selectedLocations.filter(loc => {
          // Remove if it matches the id:name format or just the name
          return !loc.startsWith(`${location.id}:`) && 
                 loc !== location.name && 
                 !loc.endsWith(`:${location.name}`);
        });
        onSelectLocations(newLocations);
        return newIds;
      } else {
        // Select
        const newIds = [...prev, location.id];
        // Check if location already exists in different format
        const exists = selectedLocations.some(loc => 
          loc === locationString || 
          loc === location.name || 
          loc.endsWith(`:${location.name}`) ||
          loc.startsWith(`${location.id}:`)
        );
        if (!exists) {
          const newLocations = [...selectedLocations, locationString];
          onSelectLocations(newLocations);
        }
        return newIds;
      }
    });
  };

  const handleSelectAll = (locations: Location[]) => {
    const allSelected = locations.every(loc => selectedLocationIds.includes(loc.id));
    
    if (allSelected) {
      // Deselect all
      const newIds = selectedLocationIds.filter(id => 
        !locations.some(loc => loc.id === id)
      );
      const newLocations = selectedLocations.filter(loc => {
        const parts = loc.split(':');
        const locId = parts.length > 1 ? parseInt(parts[0]) : null;
        const locName = parts.length > 1 ? parts[1] : loc;
        return !locations.some(l => l.id === locId || l.name === locName);
      });
      setSelectedLocationIds(newIds);
      onSelectLocations(newLocations);
    } else {
      // Select all
      const newLocations = [...selectedLocations];
      const newIds = [...selectedLocationIds];
      
      locations.forEach(loc => {
        if (!newIds.includes(loc.id)) {
          newIds.push(loc.id);
          const locationString = `${loc.id}:${loc.name}`;
          // Check if already exists in different format
          const exists = newLocations.some(l => 
            l === locationString || l === loc.name || l.endsWith(`:${loc.name}`)
          );
          if (!exists) {
            newLocations.push(locationString);
          }
        }
      });
      
      setSelectedLocationIds(newIds);
      onSelectLocations(newLocations);
    }
  };

  const filteredCities = cities.filter(city =>
    city.name.toLowerCase().includes(citySearchQuery.toLowerCase())
  );

  const filteredAreas = areas.filter(area =>
    area.name.toLowerCase().includes(areaSearchQuery.toLowerCase())
  );

  const getDisplayLocations = () => {
    // Show areas if a city or district is selected
    if (selectedCity || selectedDistrict) {
      return areas;
    }
    // Show cities if a state is selected
    if (selectedState) {
      return cities;
    }
    // Show states if a country is selected
    if (selectedCountry) {
      return states;
    }
    // Default: show cities (main level for selection)
    return cities;
  };

  const getLeftColumnTitle = () => {
    if (selectedCountry) return 'Select State or Division';
    return 'Select City or Division';
  };

  const getRightColumnTitle = () => {
    if (selectedCity) {
      return `Select a local area within ${selectedCity.name}`;
    }
    if (selectedState) {
      return `Select a city within ${selectedState.name}`;
    }
    if (selectedCountry) {
      return `Select a state within ${selectedCountry.name}`;
    }
    return 'Select a local area';
  };

  if (!isOpen) return null;

  const displayLocations = getDisplayLocations();
  const leftColumnLocations = selectedCountry ? states : cities;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {getLeftColumnTitle()}
              </h2>
              {selectedCity && (
                <h2 className="text-lg font-semibold text-gray-900">
                  {getRightColumnTitle()}
                </h2>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* GPS Search Section */}
          <div className="px-6 py-4 border-b border-gray-200 bg-blue-50">
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-blue-600" />
              Search by GPS Location
            </h3>

            <div className="space-y-4">
              {/* Current GPS status */}
              {(useGPS || (latitudeFromState && longitudeFromState)) && (
                <div className="bg-blue-100 border border-blue-300 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">
                        GPS Search Active
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        Searching within {radius}km of your location
                      </p>
                      {latitudeFromState && longitudeFromState && (
                        <p className="text-xs text-blue-600 mt-1">
                          Lat: {latitudeFromState.toFixed(6)}, Lng: {longitudeFromState.toFixed(6)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={handleClearGPS}
                      className="ml-3 text-blue-700 hover:text-blue-900 text-xs font-medium"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* Radius Slider */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    Search Radius
                  </label>
                  <span className="text-sm font-semibold text-blue-600">
                    {radius} km
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="1000"
                  step="1"
                  value={radius}
                  onChange={handleRadiusChange}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 km</span>
                  <span>500 km</span>
                  <span>1000 km</span>
                </div>
              </div>

              {/* Geolocation Button */}
              <button
                onClick={handleGetLocation}
                disabled={isLoadingLocation}
                className="w-full flex items-center justify-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium rounded-lg transition-colors duration-200 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
              >
                {isLoadingLocation ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Getting your location...
                  </>
                ) : (
                  <>
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    Use My Current Location
                  </>
                )}
              </button>

              <p className="text-xs text-gray-600 text-center">
                Or select city/area below for location-based search
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="flex h-[600px]">
            {/* Left Column - Cities/States */}
            <div className="w-1/2 border-r border-gray-200 overflow-y-auto">
              <div className="p-4">
                {selectedCountry && (
                  <div className="mb-4">
                    <button
                      onClick={() => {
                        setSelectedCountry(null);
                        setSelectedState(null);
                        setSelectedCity(null);
                        setSelectedDistrict(null);
                        setAreas([]);
                      }}
                      className="text-sm text-primary-600 hover:text-primary-800 mb-2"
                    >
                      ← Back to Countries
                    </button>
                  </div>
                )}
                
                {!selectedCountry && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Countries</h3>
                    {countries.map((country) => (
                      <button
                        key={country.id}
                        onClick={() => handleCountrySelect(country)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-100 rounded"
                      >
                        <span className="text-sm text-gray-700">{country.name}</span>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {selectedCountry && !selectedState && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">States/Divisions</h3>
                    {states.map((state) => (
                      <button
                        key={state.id}
                        onClick={() => handleStateSelect(state)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-100 rounded"
                      >
                        <span className="text-sm text-gray-700">{state.name}</span>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}

                {!selectedCountry && (
                  <>
                    <div className="mb-4">
                      <div className="mb-2 relative">
                        <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          value={citySearchQuery}
                          onChange={(e) => setCitySearchQuery(e.target.value)}
                          placeholder="Search cities..."
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Cities</h3>
                      {filteredCities.length > 0 && (
                        <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer mb-2">
                          <input
                            type="checkbox"
                            checked={filteredCities.every(city => 
                              selectedLocationIds.includes(city.id) || 
                              selectedLocations.some(loc => 
                                loc === `${city.id}:${city.name}` || 
                                loc === city.name || 
                                loc.endsWith(`:${city.name}`)
                              )
                            )}
                            onChange={() => handleSelectAll(filteredCities)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Select All Cities</span>
                        </label>
                      )}
                      {filteredCities.length === 0 ? (
                        <p className="text-sm text-gray-500 py-4">No cities found</p>
                      ) : (
                        filteredCities.map((city) => (
                          <button
                            key={city.id}
                            onClick={() => handleCitySelect(city)}
                            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-100 rounded"
                          >
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedLocationIds.includes(city.id) || 
                                  selectedLocations.some(loc => 
                                    loc === `${city.id}:${city.name}` || 
                                    loc === city.name || 
                                    loc.endsWith(`:${city.name}`)
                                  )}
                                onChange={() => handleLocationToggle(city)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                              />
                              <span className="text-sm text-gray-700">{city.name}</span>
                            </div>
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}

                {selectedState && !selectedCity && (
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Cities</h3>
                    {cities
                      .filter(city => city.parent_id === selectedState.id)
                      .map((city) => (
                        <button
                          key={city.id}
                          onClick={() => handleCitySelect(city)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-100 rounded"
                        >
                          <span className="text-sm text-gray-700">{city.name}</span>
                          <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      ))}
                  </div>
                )}

                {selectedCity && !selectedDistrict && districts.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={() => {
                        setSelectedCity(null);
                        setSelectedDistrict(null);
                        setDistricts([]);
                        setAreas([]);
                      }}
                      className="text-sm text-primary-600 hover:text-primary-800 mb-2"
                    >
                      ← Back to Cities
                    </button>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Districts in {selectedCity.name}
                    </h3>
                    {districts.map((district) => (
                      <button
                        key={district.id}
                        onClick={() => handleDistrictSelect(district)}
                        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-100 rounded"
                      >
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.includes(district.id) ||
                              selectedLocations.some(loc =>
                                loc === `${district.id}:${district.name}` ||
                                loc === district.name ||
                                loc.endsWith(`:${district.name}`)
                              )}
                            onChange={() => handleLocationToggle(district)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-2"
                          />
                          <span className="text-sm text-gray-700">{district.name}</span>
                        </div>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Areas */}
            {(selectedCity || selectedDistrict) && areas.length > 0 && (
              <div className="w-1/2 overflow-y-auto">
                <div className="p-4">
                  <div className="mb-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      {selectedDistrict ? `Areas in ${selectedDistrict.name}` : `Areas in ${selectedCity?.name}`}
                    </h3>
                    <div className="mb-2 relative">
                      <MagnifyingGlassIcon className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={areaSearchQuery}
                        onChange={(e) => setAreaSearchQuery(e.target.value)}
                        placeholder="Search areas..."
                        className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                    {filteredAreas.length > 0 && (
                      <label className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer mb-2">
                        <input
                          type="checkbox"
                          checked={filteredAreas.every(area => 
                            selectedLocationIds.includes(area.id) || 
                            selectedLocations.some(loc => 
                              loc === `${area.id}:${area.name}` || 
                              loc === area.name || 
                              loc.endsWith(`:${area.name}`)
                            )
                          )}
                          onChange={() => handleSelectAll(filteredAreas)}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm text-gray-700">Select All Areas</span>
                      </label>
                    )}
                    {filteredAreas.length === 0 ? (
                      <p className="text-sm text-gray-500 py-4">No areas found</p>
                    ) : (
                      filteredAreas.map((area) => (
                        <label
                          key={area.id}
                          className="flex items-center px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedLocationIds.includes(area.id) || 
                              selectedLocations.some(loc => 
                                loc === `${area.id}:${area.name}` || 
                                loc === area.name || 
                                loc.endsWith(`:${area.name}`)
                              )}
                            onChange={() => handleLocationToggle(area)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">{area.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {(!selectedCity && !selectedDistrict) || (selectedCity && districts.length > 0 && !selectedDistrict) || areas.length === 0 ? (
              <div className="w-1/2 flex items-center justify-center bg-gray-50">
                <div className="text-center">
                  <MapPinIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    {selectedCity && districts.length > 0 && !selectedDistrict
                      ? 'Select a district to view areas'
                      : selectedCity && areas.length === 0
                      ? 'No areas available'
                      : selectedState
                      ? 'Select a city to view districts/areas'
                      : selectedCountry
                      ? 'Select a state to view cities'
                      : 'Select a city to view local areas'}
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="text-sm text-gray-600">
              {selectedLocationIds.length > 0 && (
                <span>{selectedLocationIds.length} location(s) selected</span>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setSelectedLocationIds([]);
                  onSelectLocations([]);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Clear All
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSelectionModal;

